import { useGet, usePost } from "../../../packages/hooks";
import { VideoReview } from "../../../types";
import { UseVideoReviewsReturn } from "../types/video-reviews-types";
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { API_ENDPOINTS } from "../../../config/api-endpoints";
import { buildApiUrl } from "../../../packages/hooks/hook-utils";
import { getAuthHeaders } from "../../../utils/auth-utils";
import { useAuthStore } from "../../../store/useAuth";
import {
  handleHttpError,
  extractErrorMessage,
} from "../../../utils/error-utils";

/**
 * Helper function to parse video reviews from various API response structures
 * Centralizes response parsing logic to handle multiple possible API response formats
 * @param data - The API response data
 * @returns Object with normalized review list and total count
 */
const parseVideoReviewsResponse = (
  data: any
): { reviewList: VideoReview[]; totalCount: number } => {
  let reviewList: VideoReview[] = [];
  let totalCount = 0;

  if (!data) {
    return { reviewList, totalCount };
  }

  // Preferred canonical format: { videoReviews: [...], pagination: { total: n } }
  if (Array.isArray(data?.videoReviews)) {
    reviewList = data.videoReviews;
    totalCount = data?.pagination?.total || reviewList.length;
  }
  // Nested data format: { data: { videoReviews: [...], pagination: { total: n } } }
  else if (Array.isArray(data?.data?.videoReviews)) {
    reviewList = data.data.videoReviews;
    totalCount = data?.data?.pagination?.total || reviewList.length;
  }
  // Direct array format: [...]
  else if (Array.isArray(data)) {
    reviewList = data;
    totalCount = data.length;
  }
  // Nested data array: { data: [...] }
  else if (Array.isArray(data?.data)) {
    reviewList = data.data;
    totalCount = data.data.length;
  }
  // Legacy nested format: { data: { data: [...] } }
  else if (Array.isArray(data?.data?.data)) {
    reviewList = data.data.data;
    totalCount = data.data.data.length;
    console.warn(
      "Using legacy nested data format. Consider updating API to use canonical format."
    );
  } else {
    console.warn(
      "Unexpected video reviews response format. Expected { videoReviews: [...], pagination: { total: n } }",
      data
    );
  }

  return { reviewList, totalCount };
};

/**
 * Custom hook for video review management using hooks from @hooks/ package
 * @returns Video review state and management functions
 */
export const useVideoReviews = (): UseVideoReviewsReturn => {
  const [reviews, setReviews] = useState<VideoReview[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);

  // Initialize hooks
  const reviewsQuery = useGet<{
    videoReviews: VideoReview[];
    pagination: { total: number };
  }>(API_ENDPOINTS.VIDEO_REVIEWS.LIST, {
    onSuccess: (data) => {
      const { reviewList, totalCount } = parseVideoReviewsResponse(data);
      setReviews(reviewList);
      setTotalItems(totalCount);
    },
    onError: (error) => {
      console.error("Video Reviews API error:", error);
      toast.error("Ошибка при загрузке видео-отзывов");
    },
  });

  const createMutation = usePost<VideoReview>(
    API_ENDPOINTS.VIDEO_REVIEWS.LIST,
    {
      onSuccess: () => {
        reviewsQuery.refetch();
      },
      onError: () => {},
    }
  );

  const fetchVideoReviews = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      await reviewsQuery.refetch();
    } catch (error) {
      console.error("Error fetching video reviews:", error);
      toast.error("Ошибка при загрузке видео-отзывов");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Don't include reviewsQuery - it changes on every render

  const handleCreateVideoReview = async (
    values: Partial<VideoReview>
  ): Promise<any> => {
    try {
      setIsLoading(true);

      const response = await createMutation.mutateAsync({
        name: values.name || values.customerName || values.title || "",
        role: values.role || values.customerRole || "",
        description: values.description || values.content || "",
        video_link: values.video_link || values.videoUrl || "",
        date: values.date || values.reviewDate || "",
        image: values.image || values.thumbnail || values.thumbnailUrl || "",
        status: "PENDING",
        isActive: true,
      });
      return response;
    } catch (error) {
      console.error("Error creating video review:", error);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateVideoReview = async (
    id: string,
    values: Partial<VideoReview>
  ): Promise<any> => {
    try {
      setIsLoading(true);

      // Use the correct endpoint with video review ID for update
      const url = buildApiUrl(API_ENDPOINTS.VIDEO_REVIEWS.BY_ID(id));
      const token = useAuthStore.getState().token;

      if (!url) {
        throw new Error("URL is not available");
      }

      if (!token) {
        throw new Error("Authentication token is required");
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(API_ENDPOINTS.VIDEO_REVIEWS.BY_ID(id), token),
        },
        body: JSON.stringify({
          id: id,
          name: values.name || values.customerName || values.title || "",
          role: values.role || values.customerRole || "",
          description: values.description || values.content || "",
          video_link: values.video_link || values.videoUrl || "",
          date: values.date || values.reviewDate || "",
          image: values.image || values.thumbnail || values.thumbnailUrl || "",
          status: values.status || "PENDING",
          isActive: values.isActive ?? true,
        }),
      });

      if (!response.ok) {
        await handleHttpError(response);
      }

      await reviewsQuery.refetch();
      toast.success("Видео-отзыв успешно обновлен");
      return response;
    } catch (error) {
      console.error("Error updating video review:", error);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteVideoReview = async (id: string): Promise<boolean> => {
    try {
      setIsLoading(true);

      // Use the correct endpoint with video review ID for delete
      const url = buildApiUrl(API_ENDPOINTS.VIDEO_REVIEWS.BY_ID(id));
      const token = useAuthStore.getState().token;

      if (!url) {
        throw new Error("URL is not available");
      }

      if (!token) {
        throw new Error("Authentication token is required");
      }

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(API_ENDPOINTS.VIDEO_REVIEWS.BY_ID(id), token),
        },
      });

      if (!response.ok) {
        await handleHttpError(response, {
          404: "Видео-отзыв не найден. Возможно, он уже удален.",
          409: "Конфликт данных. Возможно, видео-отзыв используется в других местах.",
        });
      }

      await reviewsQuery.refetch();
      toast.success("Видео-отзыв успешно удален");
      return true;
    } catch (error) {
      console.error("Error deleting video review:", error);
      toast.error(
        extractErrorMessage(error, "Ошибка при удалении видео-отзыва")
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch only - avoid infinite loop
    const initialFetch = async () => {
      try {
        setIsLoading(true);
        await reviewsQuery.refetch();
      } catch (error) {
        console.error("Error fetching video reviews:", error);
        toast.error("Ошибка при загрузке видео-отзывов");
      } finally {
        setIsLoading(false);
      }
    };

    initialFetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    reviews,
    isLoading:
      isLoading ||
      reviewsQuery.isLoading ||
      reviewsQuery.isFetching ||
      createMutation.isPending,
    totalItems,
    fetchVideoReviews,
    handleCreateVideoReview,
    handleUpdateVideoReview,
    handleDeleteVideoReview,
  };
};
